import {
  Button,
  Card,
  Dialog,
  Feedback,
  Icon,
  Stack,
} from '@fashionnovateam/nova-ui-toolkit';
import * as Sentry from '@sentry/react';
import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ValidationResult } from 'joi';
import LinearProgress from '../../ReusableComponents/LinearProgress';
import AddItemsGrid from './AddItemsGrid';
import {
  ItemFieldError,
  ItemsErrors,
  ValidatedItem,
} from '../types/items.types';
import { ItemsValidationSchema } from '../validations/items.validations';
import {
  AddItemsProvider,
  useAddItems,
  useAddItemsDispatch,
} from './AddItemsContext';
import {
  useSubmitVendorCatalogItems,
  useGetRemainingItemToUpload,
  useUploadItemPhotosWithSignedUrl,
} from '../../../Lib/Hooks/ItemService';
import {
  ItemToUpload,
  ItemToUploadOutput,
} from '../../../Lib/Api/item-service';
import SampleTagsAPI from '../../../Lib/Api/sample-tags';
import { downloadCombinedPDF } from '../../SampleTags/AddTags/utils/pdfUtils';
import toast from '../../Toast/Toast';

type AddItemsStepProps = {
  errors: ItemsErrors;
  setFieldError: (
    itemId: string,
    fieldName: string,
    error: ItemFieldError,
  ) => void;
  remainingItemToUpload: number;
};

/**
 * A component that allows users to add items to a list. It displays a form
 * with interactive elements to input item details and triggers the addition of
 * new items to the list when prompted.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.errors - Object containing validation errors for the fields.
 * @param {Function} props.setFieldError - Function to set specific field errors.
 */
function AddItemsStep({
  errors,
  setFieldError,
  remainingItemToUpload,
}: AddItemsStepProps) {
  const itemsDispatch = useAddItemsDispatch();
  const items = useAddItems();

  const addNewItem = () => {
    itemsDispatch({ type: 'ADDED' });
  };

  const isItemLimitReached = remainingItemToUpload - items.length <= 0;

  return (
    <Card className="max-w-full overflow-auto h-full">
      <Card.Body className="p-6 flex flex-col gap-6 max-w-full overflow-auto h-full">
        <div className="flex flex-row items-center gap-[18px]">
          <div className="p-3 bg-primary-100 rounded-md">
            <div className="text-primary-700">
              <Icon size="24px" name="plus/plus-square" variant="solid" />
            </div>
          </div>
          <div>
            <h1 className="font-medium text-xl">Add your products</h1>
            <p className="text-xsm font-normal text-base-disabled">
              Once submitted, these products will be uploaded and sent to our
              buying team to review for purchase.
            </p>
          </div>
        </div>
        <AddItemsGrid errors={errors} setFieldError={setFieldError} />
        <Stack direction="row" content="start" gap="md" align="center">
          <Button
            disabled={isItemLimitReached}
            data-testid="add-product-button"
            color="secondary"
            onClick={addNewItem}
            className="mt-auto"
          >
            <Icon name="plus/plus-circle" variant="solid" size="16px" />
            Add Product
          </Button>
          {isItemLimitReached && (
            <p className="text-xsm font-normal text-base-disabled">
              You have reached the maximum number of items you can add.
            </p>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
}

type SubmittingStepProps = {
  numberOfSubmittedProducts: number;
};

/**
 * Renders a component that indicates the submission process for products.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {number} props.numberOfSubmittedProducts - The number of products being submitted.
 */
function SubmittingStep({ numberOfSubmittedProducts }: SubmittingStepProps) {
  return (
    <Card className="max-w-full overflow-auto max-h-full">
      <Card.Body className="p-6 flex flex-col gap-6 max-w-full overflow-auto">
        <div>
          <h1 className="font-medium text-xl">
            Submitting {numberOfSubmittedProducts} product
            {numberOfSubmittedProducts > 1 ? 's' : ''}
          </h1>
          <p className="text-xsm font-normal text-base-disabled">
            Do not close this page until the upload is finished.
          </p>
        </div>
        <LinearProgress loading />
      </Card.Body>
    </Card>
  );
}

type SubmittedStepProps = {
  onAddMoreProducts: () => void;
  onBackToDashboard: () => void;
  numberOfSubmittedItems: number;
  submittedErrors: boolean;
  onDownloadPDF: () => Promise<void>;
  isDownloadingPDF: boolean;
};

/**
 * Renders the SubmittedStep component that informs the user about the submitted products,
 * provides the number of remaining products they can submit, and offers options to
 * add more products or return to the dashboard.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {Function} props.onAddMoreProducts - Callback function executed when the "Add more products" button is clicked.
 * @param {Function} props.onBackToDashboard - Callback function executed when the "Back to dashboard" button is clicked.
 * @param {number} props.numberOfSubmittedItems - The number of products the user has successfully submitted.
 * @param {number} props.numberOfRemainingProducts - The number of products the user can still submit within the allowed limit.
 * @param {boolean} props.submittedErrors - Flag indicating if there were any errors during the submission process.
 */
function SubmittedStep({
  onAddMoreProducts,
  onBackToDashboard,
  numberOfSubmittedItems,
  submittedErrors,
  onDownloadPDF,
  isDownloadingPDF,
}: SubmittedStepProps) {
  return (
    <Card className="max-w-[657px] overflow-auto max-h-full">
      <Card.Body className="p-6 flex flex-col gap-6 max-w-full overflow-auto">
        <div className="flex flex-col items-center justify-center text-center">
          {submittedErrors ? (
            <>
              <div className="text-danger-700 bg-danger-100 rounded-full p-[24px]">
                <Icon
                  name="multiple-cross/multiple-cross-cancel-circle"
                  variant="solid"
                  size="48px"
                />
              </div>
              <div className="flex flex-col items-center justify-center mt-5 gap-1">
                <h1 className="font-medium text-xl">
                  Sorry, we&rsquo;re experiencing technical problems right now
                  and can&rsquo;t upload your products.
                </h1>
                <p className="text-xsm font-normal text-base-disabled">
                  Please try again later.
                </p>
              </div>
              <div className="flex flex-row items-center justify-center gap-3 mt-10">
                <Button
                  data-testid="back-to-dashboard-button"
                  color="secondary"
                  onClick={onBackToDashboard}
                >
                  Back to dashboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-primary-700 bg-primary-100 rounded-full p-[24px]">
                <Icon
                  name="check/check-tick-circle"
                  variant="solid"
                  size="48px"
                />
              </div>
              <div className="flex flex-col items-center justify-center mt-5 gap-1">
                <h1 className="font-medium text-xl">
                  We received your {numberOfSubmittedItems} product
                  {numberOfSubmittedItems > 1 ? 's' : ''}! Our team will review
                  and let you know what we&rsquo;re interested in!
                </h1>
              </div>
              <Feedback
                type="info"
                title="Download sample tags for all products"
                description="Attach these to all of the products you plan to send us"
                className="mt-8 mb-4 text-left w-full"
              >
                <Button
                  color="primary"
                  disabled={isDownloadingPDF}
                  onClick={onDownloadPDF}
                >
                  <Icon
                    name="download/download-down"
                    variant="solid"
                    size="16px"
                  />
                  {isDownloadingPDF ? 'Generating PDF...' : 'Download'}
                </Button>
              </Feedback>
              <div className="flex flex-row items-center justify-center gap-3 mt-10">
                <Button
                  data-testid="add-more-products-button"
                  color="secondary"
                  onClick={onAddMoreProducts}
                >
                  Add more products
                </Button>
                <Button
                  data-testid="back-to-dashboard-button"
                  color="secondary"
                  onClick={onBackToDashboard}
                >
                  Back to dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

type AddItemsStepsManagerProps = {
  isSubmitting: boolean;
  isSubmitted: boolean;
  onAddMoreProducts: () => void;
  onBackToDashboard: () => void;
  errors: ItemsErrors;
  setFieldError: (
    itemId: string,
    fieldName: string,
    error: ItemFieldError,
  ) => void;
  remainingItemToUpload: number;
  submittedErrors: boolean;
  onDownloadPDF: () => Promise<void>;
  isDownloadingPDF: boolean;
};

/**
 * Manages and renders different steps (Submitting, Submitted, or Add Items step) in the process of adding items,
 * based on the submission state and other provided inputs.
 *
 * @param {Object} props Object containing the necessary parameters for managing the steps.
 * @param {boolean} props.isSubmitting Flag indicating if the submission process is currently in progress.
 * @param {boolean} props.isSubmitted Flag indicating if the submission process has been completed.
 * @param {Function} props.onAddMoreProducts Callback function invoked when triggering adding more products after submission.
 * @param {Function} props.onBackToDashboard Callback function invoked when navigating back to the dashboard after submission.
 * @param {Object} props.errors Object containing field-specific error messages.
 * @param {Function} props.setFieldError Function to set error messages for specific fields.
 * @param {number} props.remainingItemToUpload Number of remaining products that can be added.
 * @param {boolean} props.submittedErrors Flag indicating if there were any errors during the submission process.
 */
function AddItemsStepsManager({
  isSubmitting,
  isSubmitted,
  onAddMoreProducts,
  onBackToDashboard,
  errors,
  setFieldError,
  remainingItemToUpload,
  submittedErrors,
  onDownloadPDF,
  isDownloadingPDF,
}: AddItemsStepsManagerProps) {
  const items = useAddItems();

  if (isSubmitting) {
    return <SubmittingStep numberOfSubmittedProducts={items.length} />;
  }
  if (isSubmitted) {
    return (
      <SubmittedStep
        numberOfSubmittedItems={items.length}
        onAddMoreProducts={onAddMoreProducts}
        onBackToDashboard={onBackToDashboard}
        submittedErrors={submittedErrors}
        onDownloadPDF={onDownloadPDF}
        isDownloadingPDF={isDownloadingPDF}
      />
    );
  }
  return (
    <AddItemsStep
      errors={errors}
      setFieldError={setFieldError}
      remainingItemToUpload={remainingItemToUpload}
    />
  );
}

type ConfirmSubmitModalProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  onSubmit: () => Promise<void>;
  numberOfSubmittedItems: number;
};

/**
 * ConfirmSubmitModal is a modal dialog component used to confirm the submission
 * of a specified number of items. It allows the user to cancel or confirm the action.
 *
 * @param {object} props - The properties object.
 * @param {boolean} props.isOpen - A boolean indicating whether the modal is open or not.
 * @param {function} props.setIsOpen - A function to update the open state of the modal.
 * @param {function} props.onSubmit - A callback function executed when the submit button is clicked.
 * @param {number} props.numberOfSubmittedItems - The number of items being submitted.
 */
function ConfirmSubmitModal({
  isOpen,
  setIsOpen,
  onSubmit,
  numberOfSubmittedItems,
}: ConfirmSubmitModalProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      className="max-w-[450px]"
    >
      <Dialog.Header>
        <div />
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-3">
        <h1 className="text-xl font-medium">
          Send {numberOfSubmittedItems} product
          {numberOfSubmittedItems > 1 ? 's' : ''} to Fashion Nova?
        </h1>
        <p className="text-sm font-normal text-base-disabled">
          Once you send your products, you will not be able to edit the images
          or available colors.
        </p>
      </Dialog.Content>
      <Dialog.Footer className="flex gap-2">
        <Button
          data-testid="cancel-submit-button"
          color="secondary"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          Cancel
        </Button>
        <Button data-testid="submit-button" color="primary" onClick={onSubmit}>
          Submit
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}

type AddItemsModalProps = {
  isAddItemsOpened: boolean;
  setIsAddItemsOpened: (value: boolean) => void;
  userId: string;
};

/**
 * Renders a modal component for adding items to a catalog or inventory.
 * This modal manages the steps involved in creating and validating items
 * before submission and handles modals for confirmation, errors, and completion.
 *
 * @param {object} props - The component properties.
 * @param {boolean} props.isAddItemsOpened - Controls the visibility of the AddItemsModal.
 * @param {Function} props.setIsAddItemsOpened - Function to set the visibility of the AddItemsModal.
 * @param {string} props.userId - The ID of the user performing the item addition.
 */
function AddItemsModal({
  isAddItemsOpened,
  setIsAddItemsOpened,
  userId,
}: AddItemsModalProps) {
  const items = useAddItems();
  const itemsDispatch = useAddItemsDispatch();

  const { uploadItemPhotosWithSignedUrl } = useUploadItemPhotosWithSignedUrl();
  const { mutate: submitVendorCatalogItems } = useSubmitVendorCatalogItems();
  const { data: remainingItemToUpload } = useGetRemainingItemToUpload(userId);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isConfirmSubmitModalOpen, setIsConfirmSubmitModalOpen] =
    useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<ItemsErrors>({});
  const [submittedErrors, setSubmittedErrors] = useState<boolean>(false);
  const [uploadedItemIds, setUploadedItemIds] = useState<string[]>([]);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState<boolean>(false);

  const setSubmitted = (error = false) => {
    setSubmittedErrors(error);
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  const validateItems = async (): Promise<ValidationResult<
    ValidatedItem[]
  > | null> => {
    if (items.length === 0) {
      return null;
    }

    const validation = ItemsValidationSchema.validate(items, {
      abortEarly: false,
    });
    if (validation.error) {
      const hasEmptyFile = validation.error.details.some(
        (detail) => detail.type === 'file.empty',
      );
      const hasInvalidType = validation.error.details.some(
        (detail) => detail.type === 'file.invalidType',
      );

      if (hasEmptyFile) {
        toast(
          'One or more images are empty or no longer accessible. Please re-select them.',
          { type: 'error' },
        );
      } else if (hasInvalidType) {
        toast(
          'One or more images have an invalid format. Please use JPEG or PNG.',
          { type: 'error' },
        );
      }

      setErrors(
        validation.error.details.reduce((acc, curr) => {
          const { path, message } = curr;
          const index: number = path[0] as number;
          const key: string = path[1] as string;
          return {
            ...acc,
            [`${items[index].id}-${key}`]: {
              isInvalid: true,
              message,
            },
          };
        }, {}),
      );
      return null;
    }

    // Clear all previous style_number errors before checking for duplicates
    setErrors((prevErrors) => {
      const clearedErrors = { ...prevErrors };
      Object.keys(clearedErrors).forEach((key) => {
        if (key.endsWith('-style_number')) {
          delete clearedErrors[key];
        }
      });
      return clearedErrors;
    });

    const styleNumbers = validation.value.map((item) => item.style_number);
    const hasDuplicates = styleNumbers.length !== new Set(styleNumbers).size;
    if (hasDuplicates) {
      const duplicates = styleNumbers.filter(
        (item, index) => styleNumbers.indexOf(item) !== index,
      );

      // Set errors for duplicate style numbers
      validation.value.forEach((item) => {
        if (duplicates.includes(item.style_number)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [`${item.id}-style_number`]: {
              isInvalid: true,
              message: 'Style number must be unique',
            },
          }));
        }
      });
      toast(
        {
          title: 'Style numbers must be unique.',
          text: 'You have matching style numbers, please make each unique to continue.',
        },
        { type: 'error' },
      );
      return null;
    }

    return validation;
  };

  /**
   * Callback function triggered upon form submission.
   *
   * This function handles the asynchronous validation, processing, and submission
   * of items. It also manages various UI states, such as modal visibility and
   * submission status, throughout the submission lifecycle.
   *
   * The workflow includes:
   * - Validation of input items.
   * - Processing and mapping of items to a specific format for uploading.
   * - Submission of items via `submitVendorCatalogItems`.
   * - Handling the results of the submission, including uploading associated
   *   photos and updating the UI or application state accordingly.
   *
   * The function ensures proper error handling at various stages:
   * - It stops the submission process if validation fails.
   * - Catches errors during the mapping and photo processing stages.
   * - Handles upload and API request failures gracefully by updating the submission state.
   *
   * State variables used internally:
   * - `setIsSubmitting`: Toggles the submission loading state.
   * - `setIsConfirmSubmitModalOpen`: Manages the visibility of the confirmation modal.
   * - `setSubmitted`: Tracks the final submission state.
   * - `setNumberOfRemainingProducts`: Updates the count of remaining items to be submitted.
   *
   * External dependencies:
   * - `validateItems`: Validates the items before processing.
   * - `submitVendorCatalogItems`: API function for uploading item data.
   * - `uploadItemPhotosWithSignedUrl`: Helper function to upload item photos using pre-signed URLs.
   *
   * Error handling:
   * - Stops the process and updates UI states when validation fails or mapping errors occur.
   * - If photo uploads or API calls fail, it cancels submission and appropriately updates the state.
   */
  const onSubmit = async () => {
    const validation = await validateItems();
    if (!validation || validation.error) {
      setIsSubmitting(false);
      setIsConfirmSubmitModalOpen(false);
      return;
    }

    setIsConfirmSubmitModalOpen(false);
    setIsSubmitting(true);

    let itemsToSubmit: ItemToUpload[] = [];
    const itemFiles: {
      style_number: string;
      front_file: File;
      swatch_file?: File;
    }[] = [];
    try {
      itemsToSubmit = await Promise.all(
        validation.value.map(async (item): Promise<ItemToUpload> => {
          if (!item.front_photo?.type) {
            throw new Error('Missing front photo type');
          }

          const {
            id,
            front_photo: frontPhoto,
            swatch_photo: swatchPhoto,
            ...rest
          } = item;

          itemFiles.push({
            style_number: item.style_number,
            front_file: frontPhoto,
            swatch_file: swatchPhoto,
          });

          return {
            ...rest,
            photo_content_types: {
              frontImageContentType: frontPhoto.type,
              swatchImageContentType: swatchPhoto?.type,
            },
          };
        }),
      );
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          feature: 'vendor-catalog-upload',
          type: 'item_mapping_error',
        },
        extra: {
          userId,
          numberOfItems: validation.value.length,
          itemStyleNumbers: validation.value.map((item) => item.style_number),
          errorMessage: e instanceof Error ? e.message : String(e),
        },
      });
      setSubmitted(true);
      return;
    }

    submitVendorCatalogItems(
      {
        userId,
        items: itemsToSubmit,
      },
      {
        onSuccess: async (
          data: { items: ItemToUploadOutput[] } | ItemToUploadOutput,
        ) => {
          try {
            const dataItems = 'items' in data ? data.items : [data];
            const filesWithUrl = dataItems.flatMap((item) => {
              const files = itemFiles.find(
                (itemFile) => itemFile.style_number === item.jr_vendor_ref,
              );

              if (!files) {
                throw new Error(
                  `Missing files for item with jr_vendor_ref: ${item.jr_vendor_ref} (style: ${item.style_number})`,
                );
              }

              if (!item.front_photo_upload_data?.url) {
                throw new Error(
                  `Missing front photo upload URL for item ${item.style_number}`,
                );
              }

              if (files.swatch_file && !item?.swatch_photo_upload_data?.url) {
                throw new Error(
                  `Missing swatch photo upload URL for item ${item.style_number}`,
                );
              }

              const photos = [
                {
                  url: item.front_photo_upload_data.url,
                  file: files.front_file,
                },
              ];

              if (files.swatch_file && item?.swatch_photo_upload_data?.url) {
                photos.push({
                  url: item.swatch_photo_upload_data.url,
                  file: files.swatch_file,
                });
              }

              return photos;
            });

            const result = await uploadItemPhotosWithSignedUrl({
              data: filesWithUrl,
              userId,
            });
            if (!result.success) {
              setSubmitted(true);
              return;
            }

            const vendorCatalogItemIds = dataItems.flatMap((item) =>
              item.id ? [item.id] : [],
            );
            setUploadedItemIds(vendorCatalogItemIds);
          } catch (e) {
            Sentry.captureException(e, {
              tags: {
                feature: 'vendor-catalog-upload',
                type: 'upload_processing_error',
              },
              extra: {
                userId,
                numberOfItems: itemsToSubmit.length,
                itemStyleNumbers: itemsToSubmit.map(
                  (item) => item.style_number,
                ),
                errorMessage: e instanceof Error ? e.message : String(e),
              },
            });
            setSubmitted(true);
            return;
          }

          setSubmitted(false);
        },
        onError: (error) => {
          Sentry.captureException(error, {
            tags: {
              feature: 'vendor-catalog-upload',
              type: 'api_submission_error',
            },
            extra: {
              userId,
              numberOfItems: itemsToSubmit.length,
              itemStyleNumbers: itemsToSubmit.map((item) => item.style_number),
              errorMessage:
                error instanceof Error ? error.message : String(error),
            },
          });
          setSubmitted(true);
        },
      },
    );
  };

  const setFieldError = (
    itemId: string,
    fieldName: string,
    error: ItemFieldError,
  ) => {
    setErrors({
      ...errors,
      [`${itemId}-${fieldName}`]: {
        isInvalid: error.isInvalid,
        message: error.message || '',
      },
    });
  };

  /**
   * Resets the submission state of the application. This function handles necessary state updates
   * by resetting key states to their initial values. It ensures that the application is ready
   * for a new submission cycle and clears any open submission-related modals.
   *
   * Functional Details:
   * - Disables the submission state by setting `isSubmitting` to false.
   * - Clears the submitted state by setting `isSubmitted` to false.
   * - Closes any open confirmation modals by setting `isConfirmSubmitModalOpen` to false.
   *
   * Note: The product grid cleanup might be required as part of this reset process.
   */
  const resetSteps = () => {
    itemsDispatch({ type: 'RESET' });
    setIsSubmitting(false);
    setIsSubmitted(false);
    setIsConfirmSubmitModalOpen(false);
    setUploadedItemIds([]);
    setIsDownloadingPDF(false);
  };

  /**
   * Resets the modal state and closes it.
   *
   * This function performs the following actions:
   * 1. Prevents execution if a submission is in progress by checking the `isSubmitting` flag.
   * 2. Resets steps or progression through a step-based process via the `resetSteps` function.
   * 3. Closes the modal by setting the `isAddItemsOpened` state to false.
   *
   * This ensures a clean reset state for the modal without interfering with any ongoing form submissions.
   */

  const handleDownloadPDF = async () => {
    if (uploadedItemIds.length === 0) {
      return;
    }

    setIsDownloadingPDF(true);
    try {
      const bulkCreateResponse =
        await SampleTagsAPI.bulkGetOrCreateFromVendorCatalogItems(
          userId,
          uploadedItemIds,
        );

      const { tags = [], errors: tagErrors = [] } = bulkCreateResponse;

      if (tagErrors.length > 0) {
        console.error(
          `Failed to create ${tagErrors.length} sample tags:`,
          tagErrors,
        );

        if (tags.length > 0) {
          toast(
            `${tags.length} sample tag${tags.length > 1 ? 's' : ''} generated. ${tagErrors.length} failed.`,
            { type: 'warning' },
          );
        } else {
          toast('Failed to generate sample tags');
        }
      }
      if (tags.length > 0) {
        await downloadCombinedPDF(tags, userId);
      }
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast('Failed to generate PDF');
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const resetAndCloseModal = () => {
    if (isSubmitting) return;
    resetSteps();
    setIsAddItemsOpened(false);
  };

  return (
    <Dialog
      isOpen={isAddItemsOpened}
      onClose={resetAndCloseModal}
      className="overflow-hidden h-screen w-screen max-h-none max-w-none rounded-none"
    >
      <Dialog.Header>
        {/* This div is to put the close button in the correct position (to the top right) */}
        <div />
      </Dialog.Header>
      <Dialog.Content className="bg-neutral-100 w-full flex items-center justify-center max-h-full">
        <AddItemsStepsManager
          isSubmitted={isSubmitted}
          isSubmitting={isSubmitting}
          onBackToDashboard={resetAndCloseModal}
          onAddMoreProducts={resetSteps}
          errors={errors}
          setFieldError={setFieldError}
          remainingItemToUpload={remainingItemToUpload?.count || 0}
          submittedErrors={submittedErrors}
          onDownloadPDF={handleDownloadPDF}
          isDownloadingPDF={isDownloadingPDF}
        />
      </Dialog.Content>
      <Dialog.Footer>
        {!isSubmitted && (
          <Button
            data-testid="continue-button"
            color="primary"
            onClick={async () => {
              if (await validateItems()) setIsConfirmSubmitModalOpen(true);
            }}
            disabled={isSubmitting}
          >
            Continue
          </Button>
        )}
        <ConfirmSubmitModal
          isOpen={!isSubmitted && !isSubmitting && isConfirmSubmitModalOpen}
          setIsOpen={setIsConfirmSubmitModalOpen}
          onSubmit={onSubmit}
          numberOfSubmittedItems={items.length}
        />
      </Dialog.Footer>
    </Dialog>
  );
}

function AddItemsModalContainer({
  isAddItemsOpened,
  setIsAddItemsOpened,
  userId,
}: AddItemsModalProps) {
  return (
    <AddItemsProvider
      initialItem={{
        id: uuid(),
        buyer_pick: false,
        fit_sample: false,
        fashionnova_exclusive: false,
      }}
    >
      <AddItemsModal
        userId={userId}
        isAddItemsOpened={isAddItemsOpened}
        setIsAddItemsOpened={setIsAddItemsOpened}
      />
    </AddItemsProvider>
  );
}

export default AddItemsModalContainer;
